import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState } from 'react'
import { Mail } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { AlertWithIcon } from '@/components/ui/alert'
import { useAuth } from '@/contexts/AuthContext'
import { ApiError } from '@/services/api'

export const Route = createFileRoute('/')({
  component: Login,
})

function Login() {
  const navigate = useNavigate()
  const {
    requestOTP,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Rediriger vers /projects si l'utilisateur est déjà authentifié
  React.useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate({ to: '/projects' })
    }
  }, [isAuthenticated, authLoading, navigate])

  React.useEffect(() => {
    console.log('Login component mounted')
  }, [])

  const handleSubmit = async () => {
    if (!email) {
      setError('Veuillez entrer votre adresse email')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Veuillez entrer une adresse email valide')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      // Appel API pour demander l'OTP
      await requestOTP(email)
      
      // Rediriger vers la page de vérification avec l'email en paramètre
      navigate({ 
        to: '/verify',
        search: { email }
      })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Une erreur s\'est produite lors de l\'envoi du code.')
      } else {
        setError('Une erreur s\'est produite. Veuillez réessayer.')
      }
      console.error('Erreur lors de la demande d\'OTP:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50 p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="flex flex-col items-center justify-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/assets/images/logo.png" 
                alt="Qolaig Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>

          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-800 relative inline-block">
            Portail Client
            </h1>
          </div>

          <p className="text-center text-gray-500 text-sm">
            Accès sécurisé à vos projets et métriques
          </p> 
          </div>
        

          {error && (
            <AlertWithIcon
              variant="destructive"
              description={error}
              showIcon={true}
            />
          )}

          <div className="space-y-4">
            <FormField
              label="Adresse email"
              error={error}
              required
            >
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError('')
                }}
                onKeyPress={handleKeyPress}
                placeholder="votre@email.com"
                leftIcon={<Mail className="w-4 w-4 text-cyan-400" />}
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              />
            </FormField>

            <Button
              onClick={handleSubmit}
              isLoading={isLoading}
              className="w-full bg-gradient-to-r from-cyan-400 to-cyan-500 text-white font-medium py-3 rounded-lg hover:from-cyan-500 hover:to-cyan-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none"
            >
              Recevoir le code
            </Button>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <p className="text-xs text-center text-gray-400">
              Un code de vérification sera envoyé à votre adresse email
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Besoin d'aide ?{' '}
            <a href="mailto:dev@qolaig.com" className="text-cyan-500 hover:text-cyan-600 font-medium">
              Contactez le support
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
